import { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
// eslint-disable-next-line import/no-named-as-default
import ReCAPTCHA from 'react-google-recaptcha';
import { urls } from '@zapier/identity-utils';
import { atoms, Button, Link, TextInput } from '@zapier/marketing-ui';

import { NewDeviceChallenge } from '../../hooks';
import { NEW_DEVICE_REASONS } from '../../hooks/useLogin/newDeviceReasons';
import { NewDeviceCodeStep } from '../../hooks/useLogin/useNewDeviceCodeStep';
import { maskEmail } from '../../utils/maskEmail';
import { SensitiveData } from '../global/SensitiveData';

type Props = {
  // `step.challenge`, passed on its own once the caller has checked it's set
  challenge: NewDeviceChallenge;
  email: string;
  googleCaptchaSiteKey: string;
  step: Pick<
    NewDeviceCodeStep,
    | 'captchaKey'
    | 'code'
    | 'error'
    | 'isSubmitting'
    | 'lastAction'
    | 'leave'
    | 'resend'
    | 'resendAvailableAt'
    | 'setCaptcha'
    | 'setCode'
    | 'submit'
  >;
};

// design-contract: the floor asks for Text/Heading, but @zapier/authentication
// only uses marketing-ui (per the overlay), which has neither; this matches the
// surrounding auth components' raw elements with marketing-ui type atoms.
const headingClass = `${atoms.bodyLG} ${atoms.fontSemibold} ${atoms.textCenter} ${atoms.textNeutral900}`;
const bodyClass = `${atoms.bodySM} ${atoms.textCenter} ${atoms.textNeutral800}`;
const errorClass = `${atoms.bodySM} ${atoms.textCenter} ${atoms.textError}`;
const linkButtonClass = `${atoms.bodySM} ${atoms.textNeutral800}`;

const styles = {
  form: css`
    display: grid;
    gap: var(--zds-space-16);
  `,
  captcha: css`
    display: flex;
    justify-content: center;
  `,
  // A button that looks like the other auth forms' links (marketing-ui's
  // Link without an href isn't focusable)
  linkButton: css`
    justify-self: center;
    padding: 0;
    border: none;
    background: none;
    color: inherit;
    text-decoration: underline;
    cursor: pointer;

    &:hover,
    &:disabled {
      text-decoration: none;
    }

    &:disabled {
      cursor: default;
    }
  `,
};

/**
 * Seconds left until `until` (epoch ms), updated every second
 */
function useSecondsUntil(until?: number) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    setNow(Date.now());
    if (!until || until <= Date.now()) {
      return;
    }
    const interval = setInterval(() => {
      setNow(Date.now());
      if (Date.now() >= until) {
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [until]);

  return until ? Math.max(0, Math.ceil((until - now) / 1000)) : 0;
}

/**
 * Shown instead of the login form when the password was correct but this
 * browser isn't trusted for the account yet. The user finishes signing in by
 * entering the code emailed to them, with a captcha on every submit and
 * resend. Renders a fragment so it lays out on the parent form's grid.
 *
 * The panel replaces the focused password form, so focus moves to its heading
 * on mount; otherwise it falls to <body> and screen readers miss the change.
 * Controls stay focusable while a request runs (read-only, aria-disabled),
 * and when it ends focus moves to where the user acts next.
 */
export function NewDeviceConfirmationPanel({
  challenge,
  email,
  googleCaptchaSiteKey,
  step,
}: Props) {
  const {
    captchaKey,
    code,
    error,
    isSubmitting,
    lastAction,
    resendAvailableAt,
  } = step;
  const headingRef = useRef<HTMLHeadingElement>(null);
  const codeInputRef = useRef<HTMLInputElement>(null);
  const resendRef = useRef<HTMLButtonElement>(null);
  const wasSubmitting = useRef(isSubmitting);
  const secondsUntilResend = useSecondsUntil(resendAvailableAt);
  const maskedEmail = <span className="fs-exclude">{maskEmail(email)}</span>;

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const behavior = NEW_DEVICE_REASONS[challenge.reason];
  const reasonMessage = behavior.message(challenge, lastAction);
  const needsReset = behavior.shows === 'reset';
  const canResend = secondsUntilResend === 0;

  // When a request ends: a reset is all that's left, so land on the heading
  // above its message; a locked code needs a new one; otherwise the code
  // field, to fix or type the code.
  useEffect(() => {
    if (wasSubmitting.current && !isSubmitting) {
      if (needsReset) {
        headingRef.current?.focus();
      } else if (behavior.code === 'dead' && canResend) {
        resendRef.current?.focus();
      } else {
        codeInputRef.current?.focus();
      }
    }
    wasSubmitting.current = isSubmitting;
    // Only the end of a request moves focus
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitting]);
  const codeFieldMessage = behavior.shows === 'field' ? reasonMessage : '';
  // Blank while a request runs, so the same message coming back (two wrong
  // codes in a row) is a change the live region announces again
  const statusMessage = isSubmitting
    ? ''
    : error || (behavior.shows === 'field' ? '' : reasonMessage);
  // Code field errors are shown on the field, so the live region only
  // announces them
  const announcement = isSubmitting
    ? ''
    : statusMessage || codeFieldMessage || '';

  return (
    <>
      <h2 ref={headingRef} tabIndex={-1} className={headingClass}>
        Confirm it&apos;s you
      </h2>
      {needsReset ? (
        <p className={bodyClass}>
          We couldn&apos;t confirm this device with a code for {maskedEmail}.
        </p>
      ) : !behavior.codeWasSent ? (
        // No code may have been sent (the first send failed), so don't say one was
        <p className={bodyClass}>
          To keep your account safe, we need to confirm this device with a code
          emailed to {maskedEmail}.
        </p>
      ) : (
        <p className={bodyClass}>
          To keep your account safe, enter the code we emailed to {maskedEmail}{' '}
          to finish signing in on this device.
        </p>
      )}
      {/* Always rendered, so screen readers pick up each change */}
      <p
        role="status"
        className={
          statusMessage
            ? error || behavior.shows === 'error'
              ? errorClass
              : bodyClass
            : atoms.srOnly
        }
      >
        {announcement}
      </p>
      {needsReset ? (
        <p className={bodyClass}>
          <Link href={urls.FORGOT_PASSWORD_URL}>Reset your password</Link>
        </p>
      ) : (
        <form
          css={styles.form}
          onSubmit={e => {
            if (isSubmitting) {
              e.preventDefault();
              return;
            }
            step.submit(e);
          }}
          noValidate
        >
          <SensitiveData>
            <TextInput
              ref={codeInputRef}
              id="login-device-code"
              name="device_code"
              label="Sign-in code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              required={true}
              readOnly={isSubmitting}
              error={error ? '' : codeFieldMessage}
              onChange={step.setCode}
            />
          </SensitiveData>
          <div css={styles.captcha} data-testid="new-device-captcha">
            <ReCAPTCHA
              key={captchaKey}
              sitekey={googleCaptchaSiteKey}
              onChange={step.setCaptcha}
              onExpired={() => step.setCaptcha(null)}
            />
          </div>
          <Button
            fullWidth={true}
            variant="brand"
            disabled={!code.trim()}
            aria-disabled={isSubmitting || undefined}
            type="submit"
          >
            Continue
          </Button>
          <button
            ref={resendRef}
            css={styles.linkButton}
            className={linkButtonClass}
            type="button"
            disabled={!canResend}
            aria-disabled={isSubmitting || undefined}
            onClick={() => {
              if (!isSubmitting) step.resend();
            }}
          >
            {secondsUntilResend > 0
              ? `Resend code in ${secondsUntilResend}s`
              : 'Resend code'}
          </button>
        </form>
      )}
      <button
        css={styles.linkButton}
        className={linkButtonClass}
        type="button"
        onClick={step.leave}
      >
        Back to sign in
      </button>
    </>
  );
}
